import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, FileCheck, FilePenLine, Archive, Search, Plus } from 'lucide-react';
import { ArticlesTable } from '@/components/admin/articles/ArticlesTable';
import {
  useArticlesAdmin,
  useArticlesStats,
  useArticleCategories,
  type ArticlesFilters,
} from '@/hooks/useArticlesAdmin';

/**
 * Carte de statistique simple
 */
function StatCard({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        <Icon className="w-4 h-4 text-gray-500" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
      </CardContent>
    </Card>
  );
}

export function ArticlesManagement() {
  const { t } = useTranslation('admin');

  // État des filtres
  const [filters, setFilters] = useState<ArticlesFilters>({
    status: 'all',
    category: undefined,
    lang: undefined,
    search: '',
  });

  // Récupération des données
  const { data: articles, isLoading: articlesLoading } = useArticlesAdmin(filters);
  const { data: stats, isLoading: statsLoading } = useArticlesStats();
  const { data: categories } = useArticleCategories();

  // Handlers de filtres
  const handleStatusChange = (status: string) => {
    setFilters((prev) => ({ ...prev, status: status as ArticlesFilters['status'] }));
  };

  const handleCategoryChange = (category: string) => {
    setFilters((prev) => ({
      ...prev,
      category: category === 'all' ? undefined : category,
    }));
  };

  const handleLangChange = (lang: string) => {
    setFilters((prev) => ({
      ...prev,
      lang: lang === 'all' ? undefined : lang,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('nav.articles')}</h1>
          <p className="text-gray-600 mt-2">Gestion des articles du blog</p>
        </div>

        {/* Bouton Créer article (disabled pour l'instant) */}
        <Button disabled className="bg-aspexit-green hover:bg-aspexit-green/90">
          <Plus className="w-4 h-4 mr-2" />
          Créer un article
        </Button>
      </div>

      {/* Cartes statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Total articles"
          value={stats?.total || 0}
          icon={FileText}
        />
        <StatCard
          title="Articles publiés"
          value={stats?.published || 0}
          icon={FileCheck}
        />
        <StatCard
          title="Brouillons"
          value={stats?.drafts || 0}
          icon={FilePenLine}
        />
      </div>

      {/* Filtres */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filtres</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Barre de recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher par titre ou extrait..."
              value={filters.search}
              onChange={handleSearchChange}
              className="pl-10"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtre statut (Tabs) */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Statut
              </label>
              <Tabs
                value={filters.status || 'all'}
                onValueChange={handleStatusChange}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="all">Tous</TabsTrigger>
                  <TabsTrigger value="published">Publiés</TabsTrigger>
                  <TabsTrigger value="draft">Brouillons</TabsTrigger>
                  <TabsTrigger value="archived">Archivés</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Filtre langue */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Langue
              </label>
              <Select
                value={filters.lang || 'all'}
                onValueChange={handleLangChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les langues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">Anglais</SelectItem>
                  <SelectItem value="es">Espagnol</SelectItem>
                  <SelectItem value="de">Allemand</SelectItem>
                  <SelectItem value="it">Italien</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtre catégorie */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Catégorie
              </label>
              <Select
                value={filters.category || 'all'}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {categories?.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table des articles */}
      <ArticlesTable articles={articles || []} isLoading={articlesLoading} />
    </div>
  );
}
